/**
 * THE DESTINATION BOX'S ONE CALL.
 *
 * Anthony, 2026-09-28: type a campground or an area and get the closest
 * matching campground first, or an address. The ranking, the geocoder and
 * the town reader all live behind /api/places (server/routes/places.ts);
 * this only asks, with the signed-in person's token the same way
 * src/data/rentals.ts asks /api/rentals.
 *
 * It never throws and never shows an error: a destination picker that
 * cannot reach the server offers nothing and lets the person keep typing
 * the place in by hand, which is what the box did before it had a list.
 *
 * Results come back in the order to draw them: campgrounds (kind
 * "campground", `sub` like "Pismo Beach, CA" or "near Perris, CA", `rv`
 * true for an RV park, `miles` from the yard when the yard is known),
 * then any street address (kind "address", sub "Address"), then a town or
 * zip (kind "area", sub "Area").
 */

import { api, db } from "./client.ts";
import type { PlaceResult } from "../domain/placesearch.ts";

export type { PlaceResult };

async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

/**
 * Places matching what was typed. `near` overrides where "nearest" is
 * measured from; left out, the server uses the shop's own address. Under
 * two characters there is nothing to ask and the answer is empty without
 * a request. Debounce the typing on the page (250 ms is plenty); the
 * server caches each question for ten minutes as well.
 */
export async function searchPlaces(q: string, near?: { lat: number; lng: number } | null): Promise<PlaceResult[]> {
  const text = String(q ?? "").trim();
  if (text.length < 2) return [];
  const p = new URLSearchParams({ q: text.slice(0, 120) });
  if (near && Number.isFinite(near.lat) && Number.isFinite(near.lng)) p.set("near", `${near.lat},${near.lng}`);
  try {
    const r = await fetch(api(`/api/places?${p.toString()}`), {
      headers: { Authorization: `Bearer ${await bearer()}` },
    });
    if (!r.ok) return [];
    const out = await r.json().catch(() => ({})) as { results?: PlaceResult[] };
    return Array.isArray(out.results) ? out.results : [];
  } catch {
    return [];
  }
}
